/**
 * openExternal — Office.js-aware external browser opener.
 *
 * Mirrors the classic UI's openDocumentInExternalBrowser helper
 * (RunProgress.tsx, file-private). Inlined here rather than imported
 * from RunProgress to keep Variant C self-contained — promoting that
 * helper to a shared service is a follow-up cleanup that touches
 * hot-path-adjacent component code, deferred for now.
 *
 * Why this can't just use window.open:
 *   - Office Word task pane WebView doesn't honour window.open("_blank")
 *     reliably. Depending on OS + Word version it silently no-ops,
 *     opens in the same iframe (which fails to render docx download
 *     responses), or pops a blank Office dialog.
 *   - Office.context.ui.openBrowserWindow hands the URL to the user's
 *     default external browser, which handles docx normally.
 *
 * Fallback chain:
 *   1. Office.js loaded + openBrowserWindow available → use it.
 *   2. That throws → log + fall through to step 3.
 *   3. window.open("_blank") — works in non-Office dev contexts
 *      (running the addin in a regular browser tab).
 *
 * Safe to call from any event handler. Does not throw.
 *
 * SRP: this module owns "open a URL externally". It does NOT decide
 * which URL or whether the URL is reachable — callers handle that.
 */

declare const Office:
  | {
      context?: {
        ui?: {
          openBrowserWindow?: (url: string) => void;
        };
      };
    }
  | undefined;

export function openExternal(url: string): void {
  const hasOffice = Office !== undefined;
  const hasOfficeUi = !!Office?.context?.ui && typeof Office.context.ui.openBrowserWindow === "function";
  const resolvedUrl = url.startsWith("/") && globalThis.location?.origin
    ? `${globalThis.location.origin}${url}`
    : url;

  // eslint-disable-next-line no-console
  console.info("[openExternal] invoked", {
    url,
    resolvedUrl,
    hasOffice,
    hasOfficeUi,
  });

  if (hasOfficeUi) {
    try {
      Office?.context?.ui?.openBrowserWindow?.(resolvedUrl);
      // eslint-disable-next-line no-console
      console.info("[openExternal] Office.context.ui.openBrowserWindow dispatched", { url: resolvedUrl });
      return;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[openExternal] Office.context.ui.openBrowserWindow failed; " + "falling back to window.open", err);
    }
  }
  if (globalThis.window) {
    const opened = globalThis.window.open(resolvedUrl, "_blank");
    // eslint-disable-next-line no-console
    console.info("[openExternal] window.open fallback result", {
      url: resolvedUrl,
      opened: opened !== null,
    });
  }
}
